import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider as _Divider,
  Table as _Table,
  TableBody as _TableBody,
  TableCell as _TableCell,
  TableContainer as _TableContainer,
  TableHead as _TableHead,
  TableRow as _TableRow,
  Paper as _Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  CircularProgress as _CircularProgress,
  Alert,
  Tooltip as _Tooltip,
  InputLabel as _InputLabel,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  CheckCircle as ApproveIcon,
  Delete as DeleteIcon,
  MoreVert as _MoreVertIcon,
  Assignment as TaskIcon,
  CloudUpload as _UploadIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

interface DocumentVersion {
  id: string;
  filename: string;
  version_number: number;
  is_operative: boolean;
  is_current: boolean;
  file_type: string;
  size_bytes: number;
  created_at: string;
  uploaded_by: string;
  storage_key: string;
}

interface DocumentCategory {
  id: string;
  name: string;
  color: string;
}

interface DocumentManagementModalProps {
  open: boolean;
  onClose: () => void;
  document: {
    id: string;
    name: string;
    is_operative?: boolean;
    is_current?: boolean;
    category?: DocumentCategory | null;
    custom_date?: string | null;
    date_explanation?: string;
    notes?: string;
  } | null;
  caseId: string;
  headers: any;
  versionRefreshKey?: number;
  onSave?: (documentId: string, newName: string, categoryId: string | null, customDate: string | null, dateExplanation: string, notes: string) => Promise<void>;
  onApprove?: (versionId: string) => Promise<void>;
  onDownload?: (documentId: string, filename: string) => Promise<void>;
  onDelete?: (versionId: string, versionName: string, versionNumber: number) => Promise<void>;
  onDeleteDocument?: (documentId: string) => Promise<void>;
  onRequestApproval?: (versionId: string, version: DocumentVersion, assignee: string, notes: string) => Promise<void>;
  onUploadNewVersion?: (documentId: string, file: File) => Promise<void>;
}

export default function DocumentManagementModal({
  open,
  onClose,
  document,
  caseId,
  headers,
  versionRefreshKey: _versionRefreshKey,
  onSave,
  onApprove,
  onDownload,
  onDelete,
  onDeleteDocument,
  onRequestApproval,
  onUploadNewVersion,
}: DocumentManagementModalProps) {
  const [editedName, setEditedName] = useState('');
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [_loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; versionId: string } | null>(null);
  const [_approving, setApproving] = useState<string | null>(null);
  const [_deleting, setDeleting] = useState<string | null>(null);
  const [_uploadingNewVersion, setUploadingNewVersion] = useState(false);
  const [newVersionFile, setNewVersionFile] = useState<File | null>(null);
  const [_uploadError, setUploadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [filenameError, setFilenameError] = useState<string | null>(null);

  // Approval task dialog state
  const [approvalTaskDialog, setApprovalTaskDialog] = useState<{
    open: boolean;
    versionId: string;
    versionData: any;
  }>({
    open: false,
    versionId: '',
    versionData: null,
  });
  const [approvalTaskAssignee, setApprovalTaskAssignee] = useState('');
  const [approvalTaskNotes, setApprovalTaskNotes] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [creatingApprovalTask, setCreatingApprovalTask] = useState(false);

  // Delete confirmation dialog state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Metadata fields state
  const [availableCategories, setAvailableCategories] = useState<DocumentCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | null>(null);
  const [customDate, setCustomDate] = useState<string>('');
  const [dateExplanation, setDateExplanation] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [categoryManagementOpen, setCategoryManagementOpen] = useState<boolean>(false);

  // Category management state
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [_newCategoryColor, _setNewCategoryColor] = useState<string>('#1976d2');
  const [creatingCategory, setCreatingCategory] = useState<boolean>(false);

  useEffect(() => {
    if (open && document) {
      setEditedName(document.name);
      setSelectedCategory(document.category || null);
      setCustomDate(document.custom_date || '');
      setDateExplanation(document.date_explanation || '');
      setNotes(document.notes || '');
      fetchCategories();
      // Clear any error messages when opening the modal or switching documents
      setSaveError(null);
      setUploadError(null);
      setFilenameError(null);
      // Validate the initial filename
      const validationError = validateFilename(document.name);
      setFilenameError(validationError);
    } else if (!open) {
      // Clear errors when modal closes
      setSaveError(null);
      setUploadError(null);
      setFilenameError(null);
    }
  }, [open, document]);

  const fetchVersions = async () => {
    if (!document) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/cases/${caseId}/documents/${document.id}/versions/`,
        { headers }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch versions');
      }

      const data = await response.json();
      setVersions(data.versions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `/api/cases/${caseId}/documents/categories/`,
        { headers }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      const categories = data.categories || [];
      // Sort categories alphabetically by name
      categories.sort((a: DocumentCategory, b: DocumentCategory) =>
        a.name.localeCompare(b.name)
      );
      setAvailableCategories(categories);
    } catch (err: any) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const validateFilename = (filename: string): string | null => {
    const trimmedName = filename.trim();
    if (!trimmedName) {
      return 'Document name is required';
    }

    // List of allowed file extensions
    const allowedExtensions = [
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv',
      'txt', 'rtf', 'odt', 'ods',
      'ppt', 'pptx',
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif',
      'zip', 'rar', '7z',
      'mp4', 'mov', 'avi', 'wmv',
      'mp3', 'wav', 'aac',
      'eml', 'msg',
      'html', 'htm', 'xml', 'json'
    ];

    // Extract the extension (everything after the last dot)
    const lastDotIndex = trimmedName.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === trimmedName.length - 1) {
      return 'Document name must include a valid file extension (e.g., .pdf, .docx, .xlsx)';
    }

    const extension = trimmedName.substring(lastDotIndex + 1).toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return `Invalid file extension ".${extension}". Allowed types: ${allowedExtensions.slice(0, 6).join(', ')}, etc.`;
    }

    return null;
  };

  const handleSave = async () => {
    if (!document || !onSave || !editedName.trim()) return;

    setSaving(true);
    setSaveError(null);
    try {
      await onSave(
        document.id,
        editedName.trim(),
        selectedCategory?.id || null,
        customDate || null,
        dateExplanation,
        notes
      );
      setSaveError(null);
      onClose();
    } catch (err: any) {
      console.error('Failed to save document:', err);
      // Extract error message from backend response
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save document. Please try again.';
      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (versionId: string) => {
    if (!onApprove || !document) return;

    setApproving(versionId);
    try {
      await onApprove(versionId);

      // Fetch updated versions to get the new operative version's filename
      const response = await fetch(
        `/api/cases/${caseId}/documents/${document.id}/versions/`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        const updatedVersions = data.versions || [];
        setVersions(updatedVersions);

        // Update the document name to match the newly operative version's filename
        const newOperativeVersion = updatedVersions.find((v: DocumentVersion) => v.id === versionId);
        if (newOperativeVersion) {
          setEditedName(newOperativeVersion.filename);
        }
      }
    } catch (err) {
      console.error('Failed to approve version:', err);
    } finally {
      setApproving(null);
    }
  };

  const handleDownload = async (versionId: string, filename: string) => {
    if (!onDownload) return;

    try {
      await onDownload(versionId, filename);
    } catch (err) {
      console.error('Failed to download version:', err);
    }
  };

  const handleDelete = async (versionId: string) => {
    if (!onDelete) return;

    setMenuAnchor(null);

    // Find version details
    const version = versions.find(v => v.id === versionId);
    if (!version) return;

    setDeleting(versionId);
    try {
      await onDelete(versionId, version.filename, version.version_number);
      await fetchVersions();
    } catch (err) {
      console.error('Failed to delete version:', err);
    } finally {
      setDeleting(null);
    }
  };

  const _handleDeleteDocument = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!onDeleteDocument || !document) return;

    setShowDeleteConfirmation(false);

    try {
      await onDeleteDocument(document.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  const handleRequestApproval = async (versionId: string, versionData: DocumentVersion) => {
    if (!onRequestApproval) return;

    // Fetch available users
    try {
      const response = await fetch('/api/users/', { headers });
      const data = await response.json();
      setAvailableUsers(data.results || data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }

    setApprovalTaskDialog({
      open: true,
      versionId,
      versionData,
    });
    setMenuAnchor(null);
  };

  const handleCreateApprovalTask = async () => {
    if (!approvalTaskDialog.versionData || !approvalTaskAssignee) {
      alert('Please select someone to assign the approval task to.');
      return;
    }

    setCreatingApprovalTask(true);
    try {
      await onRequestApproval?.(
        approvalTaskDialog.versionId,
        approvalTaskDialog.versionData,
        approvalTaskAssignee,
        approvalTaskNotes
      );
      setApprovalTaskDialog({ open: false, versionId: '', versionData: null });
      setApprovalTaskAssignee('');
      setApprovalTaskNotes('');
    } catch (err) {
      console.error('Failed to create approval task:', err);
    } finally {
      setCreatingApprovalTask(false);
    }
  };

  const _handleUploadNewVersion = async () => {
    if (!document || !newVersionFile || !onUploadNewVersion) return;

    setUploadingNewVersion(true);
    setUploadError(null);
    try {
      await onUploadNewVersion(document.id, newVersionFile);
      setNewVersionFile(null);
      setUploadError(null);
      await fetchVersions();
    } catch (err: any) {
      console.error('Failed to upload new version:', err);
      // Extract error message from backend response
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to upload new version. Please try again.';
      setUploadError(errorMessage);
    } finally {
      setUploadingNewVersion(false);
    }
  };

  const _handleMenuOpen = (event: React.MouseEvent<HTMLElement>, versionId: string) => {
    setMenuAnchor({ element: event.currentTarget, versionId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    // Generate a random color
    const randomColor = `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`;

    setCreatingCategory(true);
    try {
      const response = await fetch(
        `/api/cases/${caseId}/documents/categories/create/`,
        {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: newCategoryName.trim(),
            color: randomColor,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create category');
      }

      // Refresh categories list
      await fetchCategories();

      // Clear form
      setNewCategoryName('');
    } catch (err: any) {
      console.error('Failed to create category:', err);
      alert('Failed to create category. Please try again.');
    } finally {
      setCreatingCategory(false);
    }
  };

  const _handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const response = await fetch(
        `/api/cases/${caseId}/documents/categories/${categoryId}/delete/`,
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete category');
      }

      // Refresh categories list
      await fetchCategories();
    } catch (err: any) {
      console.error('Failed to delete category:', err);
      alert(err.message || 'Failed to delete category. Please try again.');
    }
  };

  const _getStatusChip = (version: DocumentVersion) => {
    if (version.is_operative) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Operative"
          color="success"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      );
    } else if (version.is_current) {
      return (
        <Chip
          icon={<ScheduleIcon />}
          label="Pending Approval"
          color="warning"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      );
    } else {
      return (
        <Chip
          label="Superseded"
          color="default"
          size="small"
          variant="outlined"
        />
      );
    }
  };

  if (!document) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit Document</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Document Name Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Document Name
            </Typography>
            <TextField
              fullWidth
              value={editedName}
              onChange={(e) => {
                const newValue = e.target.value;
                setEditedName(newValue);
                setSaveError(null); // Clear save error when user changes the name
                // Validate filename and show error if invalid
                const validationError = validateFilename(newValue);
                setFilenameError(validationError);
              }}
              placeholder="Enter document name"
              disabled={saving}
              error={!!filenameError}
              helperText={filenameError}
            />
          </Box>

          {/* Metadata Fields Section */}
          <Box mb={3}>
            {/* Category Field */}
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Category
                </Typography>
                <Button
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={() => setCategoryManagementOpen(true)}
                >
                  Manage Categories
                </Button>
              </Box>
              <Autocomplete
                options={availableCategories}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name;
                }}
                value={selectedCategory}
                onChange={async (event, newValue) => {
                  // Check if user is creating a new category
                  if (typeof newValue === 'string') {
                    // User typed a new value and pressed Enter
                    const categoryName = newValue.trim();
                    if (categoryName) {
                      // Generate a random color
                      const randomColor = `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`;

                      setCreatingCategory(true);
                      try {
                        const response = await fetch(
                          `/api/cases/${caseId}/documents/categories/create/`,
                          {
                            method: 'POST',
                            headers: {
                              ...headers,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                              name: categoryName,
                              color: randomColor,
                            }),
                          }
                        );

                        if (!response.ok) {
                          throw new Error('Failed to create category');
                        }

                        const data = await response.json();
                        const newCategory = data.category;

                        // Refresh categories list
                        await fetchCategories();

                        // Select the newly created category
                        setSelectedCategory(newCategory);
                      } catch (err: any) {
                        console.error('Failed to create category:', err);
                        alert('Failed to create category. Please try again.');
                      } finally {
                        setCreatingCategory(false);
                      }
                    }
                  } else {
                    setSelectedCategory(newValue);
                  }
                }}
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select or create a category"
                    size="small"
                  />
                )}
                renderOption={(props, option) => {
                  if (typeof option === 'string') {
                    return (
                      <li {...props}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AddIcon fontSize="small" />
                          <Typography>Create "{option}"</Typography>
                        </Box>
                      </li>
                    );
                  }
                  return (
                    <li {...props}>
                      <Chip
                        label={option.name}
                        size="small"
                        sx={{
                          backgroundColor: option.color,
                          color: '#fff',
                          fontWeight: 500,
                        }}
                      />
                    </li>
                  );
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) =>
                    option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                  );

                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some((option) => inputValue.toLowerCase() === option.name.toLowerCase());
                  if (inputValue !== '' && !isExisting) {
                    filtered.push(inputValue as any);
                  }

                  return filtered;
                }}
              />
            </Box>

            {/* Date and Date Explanation Fields */}
            <Box mb={2} display="flex" gap={2}>
              <Box flex={1}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box flex={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date Explanation
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g., Date filed, Date received"
                  value={dateExplanation}
                  onChange={(e) => setDateExplanation(e.target.value)}
                />
              </Box>
            </Box>

            {/* Notes Field */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Add notes, description, open questions, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Box>
          </Box>

          {saveError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {saveError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editedName.trim() || !!filenameError}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Actions Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {onDownload && (
          <MenuItem onClick={() => {
            const version = versions.find(v => v.id === menuAnchor?.versionId);
            if (version) handleDownload(version.id, version.filename);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        )}

        {onApprove && menuAnchor && !versions.find(v => v.id === menuAnchor.versionId)?.is_operative && (
          <MenuItem onClick={() => {
            if (menuAnchor) handleApprove(menuAnchor.versionId);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <ApproveIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Approve as Operative</ListItemText>
          </MenuItem>
        )}

        {onRequestApproval && menuAnchor && (
          <MenuItem onClick={() => {
            const version = versions.find(v => v.id === menuAnchor?.versionId);
            if (version) handleRequestApproval(menuAnchor.versionId, version);
          }}>
            <ListItemIcon>
              <TaskIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Request Approval</ListItemText>
          </MenuItem>
        )}

        {onDelete && (
          <MenuItem
            onClick={() => {
              if (menuAnchor) handleDelete(menuAnchor.versionId);
            }}
            disabled={menuAnchor ? versions.find(v => v.id === menuAnchor.versionId)?.is_operative : false}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              {menuAnchor && versions.find(v => v.id === menuAnchor.versionId)?.is_operative
                ? 'Cannot Delete Operative'
                : 'Delete Version'}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Approval Task Dialog */}
      <Dialog
        open={approvalTaskDialog.open}
        onClose={() => setApprovalTaskDialog({ open: false, versionId: '', versionData: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Document Approval</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Create a task to request approval for this document version
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ mb: 1 }}>Assign To</Typography>
              <Select
                value={approvalTaskAssignee}
                onChange={(e) => setApprovalTaskAssignee(e.target.value)}
                displayEmpty
              >
                <MenuItem value=""><em>Select user...</em></MenuItem>
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Additional Notes (Optional)"
              value={approvalTaskNotes}
              onChange={(e) => setApprovalTaskNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalTaskDialog({ open: false, versionId: '', versionData: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateApprovalTask}
            variant="contained"
            disabled={!approvalTaskAssignee || creatingApprovalTask}
          >
            {creatingApprovalTask ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDeleteDocument}
        filename={document?.name || ''}
        fileSize={undefined}
      />

      {/* Category Management Dialog */}
      <Dialog
        open={categoryManagementOpen}
        onClose={() => setCategoryManagementOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Manage Categories</Typography>
            <IconButton onClick={() => setCategoryManagementOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create and manage document categories for organizing your files.
            </Typography>

            {/* Add new category form */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Add New Category</Typography>
              <Box display="flex" gap={2} alignItems="flex-end">
                <TextField
                  label="Category Name"
                  size="small"
                  fullWidth
                  placeholder="e.g., Motion, Evidence"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateCategory}
                  disabled={!newCategoryName.trim() || creatingCategory}
                >
                  {creatingCategory ? 'Adding...' : 'Add'}
                </Button>
              </Box>
            </Box>

            {/* List of existing categories */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing Categories</Typography>
            {availableCategories.length === 0 ? (
              <Alert severity="info">No categories yet. Create one above to get started.</Alert>
            ) : (
              <Box>
                {availableCategories.map((category) => (
                  <Box
                    key={category.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1.5,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          backgroundColor: category.color,
                        }}
                      />
                      <Typography>{category.name}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryManagementOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
